import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { RootState } from "../../../redux/store";
import {
  Country,
  CountryPayload,
  CountryState,
} from "./types/country-types";

const API_BASE = process.env.REACT_APP_API_URL || "";

async function handleResp(res: Response) {
  const text = await res.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }
  if (!res.ok) {
    const msg =
      (payload && (payload.message || payload.error)) ||
      res.statusText ||
      "Request failed";
    throw new Error(msg);
  }
  return payload;
}

export const fetchCountries = createAsyncThunk<
  Country[],
  { page?: number; perPage?: number; search?: string } | undefined,
  { rejectValue: string; state: RootState }
>(
  "countries/fetch",
  async (params = { page: 1, perPage: 50 }, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const token = (state as any).auth?.token;
    if (!token) return rejectWithValue("Unauthorized");

    try {
      const url = new URL(`${API_BASE}/countries`);
      url.searchParams.set("page", String(params.page ?? 1));
      url.searchParams.set("per_page", String(params.perPage ?? 50));
      if (params.search) url.searchParams.set("search", params.search);

      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload: any = await handleResp(res);
      if (Array.isArray(payload)) return payload;
      if (payload?.data?.data && Array.isArray(payload.data.data))
        return payload.data.data;
      if (payload?.data && Array.isArray(payload.data)) return payload.data;
      return [];
    } catch (err: any) {
      return rejectWithValue(err?.message ?? "Failed to fetch countries");
    }
  }
);

export const createCountry = createAsyncThunk<
  Country,
  CountryPayload,
  { rejectValue: string; state: RootState }
>("countries/create", async (payload, { rejectWithValue, getState }) => {
  const state = getState() as RootState;
  const token = (state as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const res = await fetch(`${API_BASE}/countries`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    const data: any = await handleResp(res);
    return data?.data ?? data;
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to create country");
  }
});

export const updateCountry = createAsyncThunk<
  Country,
  { id: number } & Partial<CountryPayload>,
  { rejectValue: string; state: RootState }
>("countries/update", async ({ id, ...payload }, { rejectWithValue, getState }) => {
  const state = getState() as RootState;
  const token = (state as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const res = await fetch(`${API_BASE}/countries/${id}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    const data: any = await handleResp(res);
    return data?.data ?? data;
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to update country");
  }
});

export const deleteCountry = createAsyncThunk<
  number,
  number,
  { rejectValue: string; state: RootState }
>("countries/delete", async (id, { rejectWithValue, getState }) => {
  const state = getState() as RootState;
  const token = (state as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const res = await fetch(`${API_BASE}/countries/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    await handleResp(res);
    return id;
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to delete country");
  }
});

const initialState: CountryState = {
  items: [],
  loading: false,
  saving: false,
  error: null,
};

const countrySlice = createSlice({
  name: "countries",
  initialState,
  reducers: {
    clearCountryError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCountries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch countries";
      })
      .addCase(createCountry.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createCountry.fulfilled, (state, action) => {
        state.saving = false;
        state.items = [action.payload, ...state.items];
      })
      .addCase(createCountry.rejected, (state, action) => {
        state.saving = false;
        state.error =
          (action.payload as string) || "Failed to create country";
      })
      .addCase(updateCountry.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateCountry.fulfilled, (state, action) => {
        state.saving = false;
        const idx = state.items.findIndex((c) => c.id === action.payload.id);
        if (idx >= 0) state.items[idx] = action.payload;
      })
      .addCase(updateCountry.rejected, (state, action) => {
        state.saving = false;
        state.error =
          (action.payload as string) || "Failed to update country";
      })
      .addCase(deleteCountry.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(deleteCountry.fulfilled, (state, action) => {
        state.saving = false;
        state.items = state.items.filter((c) => c.id !== action.payload);
      })
      .addCase(deleteCountry.rejected, (state, action) => {
        state.saving = false;
        state.error =
          (action.payload as string) || "Failed to delete country";
      });
  },
});

export const { clearCountryError } = countrySlice.actions;

export const selectCountries = (state: RootState) => state.countries?.items;
export const selectCountriesLoading = (state: RootState) =>
  state.countries?.loading;
export const selectCountriesSaving = (state: RootState) =>
  state.countries?.saving;
export const selectCountriesError = (state: RootState) =>
  state.countries?.error;

export default countrySlice.reducer;
